import { Column } from '@qtek/shared/models';

export function summarizeGroup<T>(
  rows: T[],
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any,
  columns: Column[]
): T {
  if (rows.length === 0) {
    return {} as T;
  }

  const summarized: any = {};
  const firstItem = rows[0];

  // Helper to set value at a path (handles dot notation and nested objects)
  const setValueAtPath = (obj: any, path: string, value: number) => {
    const parts = path.split('.');
    let current = obj;

    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (
        !(part in current) ||
        typeof current[part] !== 'object' ||
        current[part] === null
      ) {
        current[part] = {};
      }
      current = current[part];
    }

    current[parts[parts.length - 1]] = value;
  };

  // Helper to detect the data structure pattern from the first row and column.id
  const detectStructure = (
    columnId: string
  ): 'schema-based' | 'params-based' | 'simple' | 'dot-notation' => {
    // Check for params pattern
    if (columnId.startsWith('params.')) {
      return 'params-based';
    }

    // Check for entity pattern (~<entity>.property - direct property access)
    if (columnId.startsWith('~')) {
      const firstDotIndex = columnId.indexOf('.');
      if (firstDotIndex !== -1) {
        return 'simple';
      }
    }

    // Check for schema-based pattern (schema.property with namespace/data.data)
    const lastDotIndex = columnId.lastIndexOf('.');
    if (lastDotIndex !== -1) {
      // Check if first row has the namespace/data.data structure
      if ((firstItem as any)?.namespace && (firstItem as any)?.data?.data) {
        return 'schema-based';
      }
    }

    // Check if columnId contains dots (dot notation path)
    if (columnId.includes('.')) {
      return 'dot-notation';
    }

    return 'simple';
  };

  // Helper to set value in the correct structure based on detected pattern
  const setValue = (
    columnId: string,
    value: number,
    structure:
      | 'params-based'
      | 'schema-based'
      | 'simple'
      | 'dot-notation' = 'simple'
  ) => {
    switch (structure) {
      case 'params-based': {
        // For params.property format
        if (!summarized.params) {
          summarized.params = {};
        }
        summarized.params[columnId.replace('params.', '')] = value;
        return;
      }

      case 'schema-based': {
        // For schema.property fvalueAccessorormat with namespace/data.data structure
        const splitId = (id: string): { schema: string; property: string } => {
          const lastDotIndex = id.lastIndexOf('.');
          const schema = id.slice(0, lastDotIndex);
          const property = id.slice(lastDotIndex + 1);
          return { schema, property };
        };

        const { schema, property } = splitId(columnId);
        const namespace = (firstItem as any).namespace;
        const fullPath = `${namespace[schema] || schema}.${property}`;

        if (!summarized.data) {
          summarized.data = {};
        }
        if (!summarized.data.data) {
          summarized.data.data = {};
        }

        summarized.data.data[fullPath] = value;
        return;
      }

      case 'simple': {
        // For direct property access or ~<entity>.property pattern
        if (columnId.startsWith('~')) {
          const firstDotIndex = columnId.indexOf('.');
          if (firstDotIndex !== -1) {
            const property = columnId.slice(firstDotIndex + 1);
            summarized[property] = value;
          } else {
            summarized[columnId] = value;
          }
        } else {
          summarized[columnId] = value;
        }
        return;
      }

      case 'dot-notation':
      default: {
        // For nested paths using dot notation (e.g., "location.city", "asset.data.property")
        setValueAtPath(summarized, columnId, value);
        return;
      }
    }
  };

  for (const column of columns) {
    if (!column.grouping) {
      continue;
    }

    const columnStructure = detectStructure(column.id);

    let calculatedValue: number;

    if (column.grouping === 'sum') {
      calculatedValue = rows.reduce((acc, row) => {
        const val = valueAccessor(row, column.id);
        const num = Number(val);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
    } else if (column.grouping === 'avg') {
      const sum = rows.reduce((acc, row) => {
        const val = valueAccessor(row, column.id);
        const num = Number(val);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);
      calculatedValue = rows.length > 0 ? sum / rows.length : 0;
    } else {
      continue;
    }

    setValue(column.id, calculatedValue, columnStructure);
  }

  return summarized as T;
}

// Helper to get value at path from source object
export function getValueAtPath(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = current[part];
  }
  return current;
}

// Helper to set value at path in target object (creates nested structure)
export function setValueAtPath(target: any, path: string, value: any) {
  const parts = path.split('.');
  let current = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (
      !(part in current) ||
      typeof current[part] !== 'object' ||
      current[part] === null
    ) {
      current[part] = {};
    }
    current = current[part];
  }
  // Deep clone the value to avoid reference issues
  current[parts[parts.length - 1]] =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? JSON.parse(JSON.stringify(value))
      : value;
}
